import { useEffect } from 'react';
import {
  acknowledgeGoogleAdsBeginCheckoutConversion,
  claimGoogleAdsBeginCheckoutConversion,
} from '@/api/google-ads';
import { authClient } from '@/auth/client';
import { clientEnv } from '@/env/client';
import { normalizeGoogleAdsEnhancedConversionEmail } from './google-ads-signup-policy';

type GoogleAdsUserData = {
  email: string;
};

type Gtag = (
  ...args:
    | [
        command: 'event',
        eventName: 'conversion',
        params: {
          send_to: string;
          transaction_id: string;
          value: number;
          currency: string;
          event_callback?: () => void;
        },
      ]
    | [command: 'js', date: Date]
    | [command: 'config', tagId: string]
    | [command: 'set', target: 'user_data', data: GoogleAdsUserData]
) => void;

type WindowWithGtag = Window & {
  dataLayer?: unknown[];
  gtag?: Gtag;
  googleAdsBeginCheckoutE2EEnabled?: boolean;
  googleAdsBeginCheckoutTagBootstrapped?: boolean;
};

export type GoogleAdsBeginCheckoutTracking = {
  conversionId: string | null;
  transactionId: string;
  enhancedConversionEmail: string;
  value: number;
  currency: string;
};

type BeginCheckoutConversion = Omit<
  GoogleAdsBeginCheckoutTracking,
  'conversionId'
> & {
  onAcknowledged: () => void;
};

type ConversionDispatch = {
  cancel: () => void;
  started: boolean;
};

const BEGIN_CHECKOUT_CONVERSION_SEND_DELAYS_MS = [0, 500, 1000] as const;
const BEGIN_CHECKOUT_CLAIM_MAX_WAIT_MS = 500;
const BEGIN_CHECKOUT_NAVIGATION_MAX_WAIT_MS = 1500;
const CLAIM_FAILURE_RETRY_MS = 30 * 1000;
const BEGIN_CHECKOUT_RETRY_EVENT = 'google-ads-begin-checkout-retry';

export function GoogleAdsBeginCheckoutTracker() {
  const { data: session, isPending } = authClient.useSession();
  const userId = session?.user?.id;

  useEffect(() => {
    if (!isGoogleAdsBeginCheckoutRuntimeEnabled() || isPending || !userId) {
      return;
    }

    let stopped = false;
    let running = false;
    let retryHandle: number | null = null;
    let cancelDispatch: (() => void) | null = null;

    const cancelRetry = () => {
      if (retryHandle !== null) window.clearTimeout(retryHandle);
      retryHandle = null;
    };
    const scheduleRetry = (delayMs: number) => {
      if (stopped) return;
      cancelRetry();
      retryHandle = window.setTimeout(runAttempt, Math.max(delayMs, 1000));
    };
    const stopTracking = () => {
      stopped = true;
      cancelRetry();
      cancelDispatch?.();
    };
    const acknowledge = async (attemptId: string) => {
      try {
        const result = await acknowledgeGoogleAdsBeginCheckoutConversion({
          data: { attemptId },
        });
        if (!isAcknowledgementResult(result) || !result.acknowledged) {
          scheduleRetry(CLAIM_FAILURE_RETRY_MS);
          return;
        }

        cancelDispatch?.();
        cancelDispatch = null;
        scheduleRetry(1000);
      } catch {
        scheduleRetry(CLAIM_FAILURE_RETRY_MS);
      }
    };
    async function runAttempt() {
      if (stopped || running) return;
      running = true;
      try {
        const claim = await claimGoogleAdsBeginCheckoutConversion({
          data: {},
        });
        if (!isBeginCheckoutClaimResult(claim)) {
          scheduleRetry(CLAIM_FAILURE_RETRY_MS);
          return;
        }
        if (!claim.shouldTrack) {
          if (claim.status === 'pending' && claim.retryAfterMs) {
            scheduleRetry(claim.retryAfterMs);
          } else if (claim.status === 'missing') {
            cancelRetry();
          } else {
            scheduleRetry(1000);
          }
          return;
        }

        cancelDispatch?.();
        const dispatch = dispatchGoogleAdsBeginCheckoutConversion({
          transactionId: claim.transactionId,
          enhancedConversionEmail: claim.enhancedConversionEmail,
          value: claim.value,
          currency: claim.currency,
          onAcknowledged: () => {
            void acknowledge(claim.attemptId);
          },
        });
        cancelDispatch = dispatch.cancel;
        scheduleRetry(
          dispatch.started ? claim.retryAfterMs : CLAIM_FAILURE_RETRY_MS
        );
      } catch {
        scheduleRetry(CLAIM_FAILURE_RETRY_MS);
      } finally {
        running = false;
      }
    }
    const retryWhenActive = () => {
      if (document.visibilityState === 'visible') scheduleRetry(1000);
    };

    window.addEventListener('focus', retryWhenActive);
    window.addEventListener(BEGIN_CHECKOUT_RETRY_EVENT, retryWhenActive);
    document.addEventListener('visibilitychange', retryWhenActive);
    void runAttempt();

    return () => {
      stopTracking();
      window.removeEventListener('focus', retryWhenActive);
      window.removeEventListener(BEGIN_CHECKOUT_RETRY_EVENT, retryWhenActive);
      document.removeEventListener('visibilitychange', retryWhenActive);
    };
  }, [isPending, userId]);

  return null;
}

export function requestGoogleAdsBeginCheckoutRetry() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(BEGIN_CHECKOUT_RETRY_EVENT));
}

export function prepareGoogleAdsBeginCheckoutTag() {
  if (!isGoogleAdsBeginCheckoutRuntimeEnabled()) return false;
  const adsId = clientEnv.VITE_GOOGLE_ADS_ID;
  if (!adsId) return false;

  try {
    const gtagWindow = window as WindowWithGtag;
    ensureGtagQueue(gtagWindow);
    ensureGoogleAdsTagLoaded(gtagWindow, adsId);
    return true;
  } catch {
    return false;
  }
}

export async function trackGoogleAdsBeginCheckoutBeforeNavigation(
  tracking: GoogleAdsBeginCheckoutTracking
) {
  if (!isGoogleAdsBeginCheckoutRuntimeEnabled()) return false;

  let claim: Awaited<
    ReturnType<typeof claimGoogleAdsBeginCheckoutConversion>
  > | null = null;
  if (tracking.conversionId) {
    claim = await claimBeginCheckoutBeforeNavigation(tracking.conversionId);
  }

  if (isBeginCheckoutClaimResult(claim) && !claim.shouldTrack) {
    if (claim.status === 'acknowledged') return true;
    claim = null;
  }

  const conversion =
    isBeginCheckoutClaimResult(claim) && claim.shouldTrack
      ? {
          transactionId: claim.transactionId,
          enhancedConversionEmail: claim.enhancedConversionEmail,
          value: claim.value,
          currency: claim.currency,
        }
      : tracking;
  const attemptId =
    isBeginCheckoutClaimResult(claim) && claim.shouldTrack
      ? claim.attemptId
      : null;

  return waitForGoogleAdsBeginCheckoutDelivery({ conversion, attemptId });
}

function claimBeginCheckoutBeforeNavigation(conversionId: string) {
  return new Promise<Awaited<
    ReturnType<typeof claimGoogleAdsBeginCheckoutConversion>
  > | null>((resolve) => {
    let settled = false;
    const settle = (
      claim: Awaited<
        ReturnType<typeof claimGoogleAdsBeginCheckoutConversion>
      > | null
    ) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutHandle);
      resolve(claim);
    };
    const timeoutHandle = window.setTimeout(
      () => settle(null),
      BEGIN_CHECKOUT_CLAIM_MAX_WAIT_MS
    );

    claimGoogleAdsBeginCheckoutConversion({ data: { conversionId } }).then(
      settle,
      () => settle(null)
    );
  });
}

function waitForGoogleAdsBeginCheckoutDelivery({
  attemptId,
  conversion,
}: {
  attemptId: string | null;
  conversion: Omit<GoogleAdsBeginCheckoutTracking, 'conversionId'>;
}): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    let dispatch: ConversionDispatch = { cancel: () => {}, started: false };
    const settle = (acknowledged: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutHandle);
      dispatch.cancel();
      resolve(acknowledged);
    };
    const timeoutHandle = window.setTimeout(
      () => settle(false),
      BEGIN_CHECKOUT_NAVIGATION_MAX_WAIT_MS
    );

    dispatch = dispatchGoogleAdsBeginCheckoutConversion({
      ...conversion,
      onAcknowledged: () => {
        if (attemptId) {
          void acknowledgeGoogleAdsBeginCheckoutConversion({
            data: { attemptId },
          }).then(
            () => settle(true),
            // A later root claim will retry with the same transaction ID.
            () => settle(true)
          );
          return;
        }
        settle(true);
      },
    });

    if (!dispatch.started) settle(false);
  });
}

function dispatchGoogleAdsBeginCheckoutConversion({
  currency,
  enhancedConversionEmail,
  onAcknowledged,
  transactionId,
  value,
}: BeginCheckoutConversion): ConversionDispatch {
  if (!isGoogleAdsBeginCheckoutRuntimeEnabled()) {
    return { cancel: () => {}, started: false };
  }

  const adsId = clientEnv.VITE_GOOGLE_ADS_ID;
  const label = clientEnv.VITE_GOOGLE_ADS_BEGIN_CHECKOUT_CONVERSION_LABEL;
  if (!adsId || !label || !transactionId) {
    return { cancel: () => {}, started: false };
  }

  try {
    const gtagWindow = window as WindowWithGtag;
    ensureGtagQueue(gtagWindow);
    ensureGoogleAdsTagLoaded(gtagWindow, adsId);

    const email = normalizeGoogleAdsEnhancedConversionEmail(
      enhancedConversionEmail
    );
    if (email) {
      gtagWindow.gtag?.('set', 'user_data', { email });
    }

    let acknowledged = false;
    const retryHandles = new Set<number>();
    const cancel = () => {
      for (const handle of retryHandles) window.clearTimeout(handle);
      retryHandles.clear();
    };
    const acknowledgeOnce = () => {
      if (acknowledged) return;
      acknowledged = true;
      cancel();
      onAcknowledged();
    };
    const sendConversion = () => {
      gtagWindow.gtag?.('event', 'conversion', {
        send_to: `${adsId}/${label}`,
        transaction_id: transactionId,
        value: Number.isFinite(value) ? Math.max(0, value) : 0,
        currency: currency.trim().toUpperCase(),
        event_callback: acknowledgeOnce,
      });
    };

    sendConversion();
    for (const delay of BEGIN_CHECKOUT_CONVERSION_SEND_DELAYS_MS.slice(1)) {
      const handle = window.setTimeout(() => {
        retryHandles.delete(handle);
        if (!acknowledged) sendConversion();
      }, delay);
      retryHandles.add(handle);
    }

    return { cancel, started: true };
  } catch {
    return { cancel: () => {}, started: false };
  }
}

function isBeginCheckoutClaimResult(
  value: unknown
): value is Awaited<ReturnType<typeof claimGoogleAdsBeginCheckoutConversion>> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'shouldTrack' in value &&
    'status' in value
  );
}

function isAcknowledgementResult(
  value: unknown
): value is { acknowledged: boolean } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'acknowledged' in value &&
    typeof value.acknowledged === 'boolean'
  );
}

function isGoogleAdsBeginCheckoutRuntimeEnabled() {
  if (import.meta.env.PROD) return true;
  if (import.meta.env.MODE !== 'e2e' || typeof window === 'undefined') {
    return false;
  }
  return (window as WindowWithGtag).googleAdsBeginCheckoutE2EEnabled === true;
}

function ensureGtagQueue(gtagWindow: WindowWithGtag) {
  gtagWindow.dataLayer = gtagWindow.dataLayer || [];
  if (typeof gtagWindow.gtag === 'function') return;

  gtagWindow.gtag = ((...args: Parameters<Gtag>) => {
    gtagWindow.dataLayer?.push(args);
  }) as Gtag;
}

function ensureGoogleAdsTagLoaded(gtagWindow: WindowWithGtag, adsId: string) {
  if (!document.getElementById('google-tag-manager')) {
    const script = document.createElement('script');
    script.id = 'google-tag-manager';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      adsId
    )}`;
    script.async = true;
    script.addEventListener(
      'error',
      () => {
        script.remove();
        gtagWindow.googleAdsBeginCheckoutTagBootstrapped = false;
      },
      { once: true }
    );
    document.head.appendChild(script);
  }

  if (gtagWindow.googleAdsBeginCheckoutTagBootstrapped) return;

  gtagWindow.gtag?.('js', new Date());
  gtagWindow.gtag?.('config', adsId);
  gtagWindow.googleAdsBeginCheckoutTagBootstrapped = true;
}
